// Pure data, layout and search helpers for the C-Level Org Chart.
//
// Everything here runs in linear time (plus a sort where noted) and never
// recurses, so a company with thousands of jobs stays responsive. Nothing in
// this module depends on React; the page memoizes each result.

// ─── Geometry ─────────────────────────────────────────────────────────────────
export const NODE_W = 210;
export const NODE_H = 110; // approx rendered height (avatar row + dept chip)
const LEVEL_GAP = 130; // vertical gap between levels
const SIBLING_GAP = 40; // extra horizontal gap between siblings
const SLOT = NODE_W + SIBLING_GAP;
const ROW = NODE_H + LEVEL_GAP;

// ─── Progressive disclosure ───────────────────────────────────────────────────
// A parent shows its children a page at a time; the rest sit behind a
// "more" card. A few extra siblings are shown rather than hidden behind it.
export const CHILD_PAGE_SIZE = 20;
const CHILD_PAGE_SLACK = 4;
// The first view expands level by level while it stays within this budget.
const INITIAL_MAX_NODES = 60;
const INITIAL_MAX_SLOTS = 30;
// A group of at least TEAM_MIN positions with no reports of their own is laid
// out as a grid (a "team block") instead of one wide row.
const TEAM_MIN = 7;
const TEAM_MAX_COLS = 6;
const TEAM_ROW = NODE_H + 40;
const TEAM_PAD = 14;

export const moreKey = (parentId) => `more:${parentId}`;

const UNREACHABLE_DEPTH = 1e9;

const lower = (value) => String(value || "").toLowerCase();

// ─── Org index ────────────────────────────────────────────────────────────────
// Adjacency, depths, subtree sizes and the main root, built in O(n).
export function buildOrgIndex(nodes, designatedRootId) {
  const byId = new Map();
  nodes.forEach((n) => byId.set(n.id, n));

  const childrenOf = new Map();
  const childIndex = new Map();
  const roots = [];
  nodes.forEach((n) => {
    if (!n.parentId) {
      roots.push(n.id);
      return;
    }
    let list = childrenOf.get(n.parentId);
    if (!list) {
      list = [];
      childrenOf.set(n.parentId, list);
    }
    childIndex.set(n.id, list.length);
    list.push(n.id);
  });

  // Depths and descendant counts: pre-order walk from each root, then sum in
  // reverse. Nodes caught in a parent cycle are unreachable from any root.
  const depth = new Map();
  const descendantCount = new Map();
  roots.forEach((rootId) => {
    const order = [];
    const stack = [rootId];
    depth.set(rootId, 0);
    while (stack.length) {
      const id = stack.pop();
      order.push(id);
      const children = childrenOf.get(id);
      if (!children) continue;
      const d = depth.get(id) + 1;
      children.forEach((c) => {
        if (depth.has(c)) return;
        depth.set(c, d);
        stack.push(c);
      });
    }
    for (let i = order.length - 1; i >= 0; i--) {
      const children = childrenOf.get(order[i]);
      let total = 0;
      if (children) children.forEach((c) => { total += 1 + (descendantCount.get(c) || 0); });
      descendantCount.set(order[i], total);
    }
  });

  // The single overall root: the designated one, else the root with the
  // largest organization beneath it.
  let mainRootId = null;
  const designated = designatedRootId != null ? String(designatedRootId) : null;
  if (designated && roots.includes(designated)) {
    mainRootId = designated;
  } else if (roots.length > 0) {
    mainRootId = roots[0];
    let best = descendantCount.get(mainRootId) || 0;
    roots.forEach((r) => {
      const count = descendantCount.get(r) || 0;
      if (count > best) {
        best = count;
        mainRootId = r;
      }
    });
  }

  const inTree = new Set();
  if (mainRootId) {
    const stack = [mainRootId];
    while (stack.length) {
      const id = stack.pop();
      if (inTree.has(id)) continue;
      inTree.add(id);
      const children = childrenOf.get(id);
      if (children) children.forEach((c) => stack.push(c));
    }
  }

  const unassigned = mainRootId ?
      nodes.filter((n) => !n.parentId && n.id !== mainRootId)
    : [];

  return {
    byId,
    childrenOf,
    childIndex,
    depth,
    descendantCount,
    mainRootId,
    inTree,
    unassigned,
    total: nodes.length,
  };
}

// Ancestor ids of `id`, root first.
export function getAncestors(index, id) {
  const path = [];
  const seen = new Set([id]);
  let cur = index.byId.get(id)?.parentId;
  while (cur && index.byId.has(cur) && !seen.has(cur)) {
    path.push(cur);
    seen.add(cur);
    cur = index.byId.get(cur).parentId;
  }
  return path.reverse();
}

export function getDescendants(index, id) {
  const out = [];
  const stack = [id];
  const seen = new Set([id]);
  while (stack.length) {
    const children = index.childrenOf.get(stack.pop());
    if (!children) continue;
    children.forEach((c) => {
      if (seen.has(c)) return;
      seen.add(c);
      out.push(c);
      stack.push(c);
    });
  }
  return out;
}

// For each node in the main tree, how many of its descendants are in `idSet`.
export function countInSubtrees(index, idSet) {
  const counts = new Map();
  if (!index.mainRootId) return counts;
  const order = [];
  const stack = [index.mainRootId];
  while (stack.length) {
    const id = stack.pop();
    order.push(id);
    const children = index.childrenOf.get(id);
    if (children) children.forEach((c) => stack.push(c));
  }
  for (let i = order.length - 1; i >= 0; i--) {
    const children = index.childrenOf.get(order[i]);
    let total = 0;
    if (children) {
      children.forEach((c) => {
        total += (idSet.has(c) ? 1 : 0) + (counts.get(c) || 0);
      });
    }
    counts.set(order[i], total);
  }
  return counts;
}

// ─── Disclosure ───────────────────────────────────────────────────────────────
export const childLimitFor = (childLimit, id) =>
  childLimit?.get(id) ?? CHILD_PAGE_SIZE;

// The children a parent currently shows: its first `limit`, plus any pinned
// ones further down (jobs the user navigated to via search).
function pickVisibleChildren(all, limit, pinned) {
  if (all.length <= limit + CHILD_PAGE_SLACK) return { ids: all, hidden: 0 };
  const ids = all.slice(0, limit);
  if (pinned && pinned.size > 0) {
    for (let i = limit; i < all.length; i++) {
      if (pinned.has(all[i])) ids.push(all[i]);
    }
  }
  return { ids, hidden: all.length - ids.length };
}

const isLeaf = (index, id) => !index.childrenOf.get(id)?.length;

// How a parent shows its children: which ones, how many are hidden, and —
// when they are many and none has reports — the columns of a team block.
function childGroup(index, parentId, limit, pinned) {
  const all = index.childrenOf.get(parentId) || [];
  const { ids, hidden } = pickVisibleChildren(all, limit, pinned);
  const count = ids.length + (hidden > 0 ? 1 : 0);
  let cols = null;
  if (count >= TEAM_MIN && ids.every((c) => isLeaf(index, c))) {
    cols = Math.min(TEAM_MAX_COLS, Math.max(3, Math.ceil(Math.sqrt(count * 2))));
  }
  return { ids, hidden, cols };
}

// Expands the tree level by level from the root while the view stays within
// budget. A small org opens fully; a large one opens its top levels only.
export function computeInitialExpanded(index) {
  const expanded = new Set();
  if (!index.mainRootId) return expanded;

  let frontier = [index.mainRootId];
  let visible = 1;
  let settledSlots = 0; // width taken by leaves, "more" cards and team blocks above the frontier
  let rootLevel = true;

  while (frontier.length) {
    const parents = [];
    const next = [];
    let added = 0;
    let levelSlots = 0;
    let leaves = 0;
    let settled = 0;
    frontier.forEach((id) => {
      const children = index.childrenOf.get(id);
      if (!children || children.length === 0) {
        leaves += 1;
        return;
      }
      const { ids, hidden, cols } = childGroup(index, id, CHILD_PAGE_SIZE, null);
      const shown = ids.length + (hidden > 0 ? 1 : 0);
      parents.push(id);
      added += shown;
      if (cols) {
        // A team block is final: its members have nobody below them.
        levelSlots += cols;
        settled += cols;
        return;
      }
      levelSlots += shown;
      if (hidden > 0) settled += 1;
      ids.forEach((c) => next.push(c));
    });
    if (parents.length === 0) break;

    const slots = settledSlots + leaves + levelSlots;
    if (!rootLevel && (visible + added > INITIAL_MAX_NODES || slots > INITIAL_MAX_SLOTS)) break;

    parents.forEach((id) => expanded.add(id));
    visible += added;
    settledSlots += leaves + settled;
    frontier = next;
    rootLevel = false;
  }
  return expanded;
}

export function allExpandable(index) {
  const expanded = new Set();
  index.childrenOf.forEach((children, id) => {
    if (children.length > 0 && index.inTree.has(id)) expanded.add(id);
  });
  return expanded;
}

// ─── Layout ───────────────────────────────────────────────────────────────────
// Classic top-down tree: leaves take one slot, parents centre over their
// children. Large teams with no reports of their own are packed into a grid
// under their manager. Only the visible part of the main tree is laid out.
export function computeTreeLayout(index, { expanded, childLimit, pinned }) {
  const root = index.mainRootId;
  const pos = new Map();
  const kids = new Map();
  const parentOf = new Map();
  const groupOf = new Map();
  if (!root || !index.byId.has(root)) {
    return { items: [], pos, kids, parentOf, groups: [], groupOf, bounds: null };
  }

  // 1. Visible structure, in pre-order.
  const depth = new Map([[root, 0]]);
  const hiddenOf = new Map();
  const teamCols = new Map();
  const order = [];
  const stack = [root];
  while (stack.length) {
    const key = stack.pop();
    order.push(key);
    if (hiddenOf.has(key) || !expanded.has(key)) continue;
    if (!index.childrenOf.get(key)?.length) continue;

    const { ids, hidden, cols } = childGroup(index, key, childLimitFor(childLimit, key), pinned);
    const d = depth.get(key) + 1;
    const list = [];
    ids.forEach((c) => {
      if (depth.has(c)) return;
      depth.set(c, d);
      list.push(c);
    });
    if (hidden > 0) {
      const mk = moreKey(key);
      hiddenOf.set(mk, hidden);
      depth.set(mk, d);
      list.push(mk);
    }
    if (list.length === 0) continue;
    kids.set(key, list);
    if (cols) teamCols.set(key, cols);
    for (let i = list.length - 1; i >= 0; i--) {
      parentOf.set(list[i], key);
      stack.push(list[i]);
    }
  }

  // 2. Subtree widths, bottom-up. A team block is as wide as its columns.
  const width = new Map();
  for (let i = order.length - 1; i >= 0; i--) {
    const key = order[i];
    const list = kids.get(key);
    let w = teamCols.get(key) || 0;
    if (!w && list) list.forEach((c) => { w += width.get(c); });
    width.set(key, w || 1);
  }

  // 3. Positions, top-down. x is the node's centre, y its top edge.
  const offset = new Map([[root, 0]]);
  const inTeam = new Map();
  const items = new Array(order.length);
  let minX = Infinity;
  let maxX = -Infinity;
  let maxY = 0;
  order.forEach((key, i) => {
    let x;
    let y;
    const cell = inTeam.get(key);
    if (cell) {
      ({ x, y } = cell);
    } else {
      x = offset.get(key) + (width.get(key) * SLOT) / 2;
      y = depth.get(key) * ROW;
    }
    pos.set(key, { x, y });
    const hidden = hiddenOf.get(key);
    items[i] = hidden != null ?
        { key, kind: "more", id: null, parentId: parentOf.get(key), x, y, hidden }
      : { key, kind: "node", id: key, parentId: parentOf.get(key) || null, x, y };
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;

    const list = kids.get(key);
    if (!list) return;
    const left = x - (width.get(key) * SLOT) / 2;
    const cols = teamCols.get(key);
    if (cols) {
      list.forEach((c, j) => {
        inTeam.set(c, {
          x: left + (j % cols) * SLOT + SLOT / 2,
          y: y + ROW + Math.floor(j / cols) * TEAM_ROW,
        });
      });
    } else {
      let cursor = left;
      list.forEach((c) => {
        offset.set(c, cursor);
        cursor += width.get(c) * SLOT;
      });
    }
  });

  // 4. One connector group per expanded parent: a drop, then a bus with a
  //    riser per child, or a team block the drop runs into.
  const groups = [];
  kids.forEach((list, key) => {
    const p = pos.get(key);
    const top = p.y + NODE_H + 2;
    const cols = teamCols.get(key);
    let group;
    if (cols) {
      const rows = Math.ceil(list.length / cols);
      const left = p.x - (cols * SLOT) / 2;
      const gx0 = left + SIBLING_GAP / 2 - TEAM_PAD;
      const gx1 = left + cols * SLOT - SIBLING_GAP / 2 + TEAM_PAD;
      const gy0 = p.y + ROW - TEAM_PAD;
      const gy1 = p.y + ROW + (rows - 1) * TEAM_ROW + NODE_H + TEAM_PAD;
      group = {
        key,
        team: true,
        count: index.childrenOf.get(key).length,
        px: p.x,
        top,
        bottom: gy1,
        minX: gx0,
        maxX: gx1,
        gx0,
        gx1,
        gy0,
        gy1,
      };
    } else {
      const bottom = p.y + ROW - 4;
      const xs = list.map((c) => pos.get(c).x);
      group = {
        key,
        team: false,
        px: p.x,
        top,
        bottom,
        mid: (top + bottom) / 2,
        minX: Math.min(p.x, xs[0]),
        maxX: Math.max(p.x, xs[xs.length - 1]),
        xs,
      };
    }
    groups.push(group);
    groupOf.set(key, group);
  });

  return {
    items,
    pos,
    kids,
    parentOf,
    groups,
    groupOf,
    bounds: {
      minX: minX - NODE_W / 2,
      maxX: maxX + NODE_W / 2,
      minY: 0,
      maxY: maxY + NODE_H,
    },
  };
}

// Bounding box of the given layout keys, or null.
export function boundsOf(layout, keys) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  keys.forEach((key) => {
    const p = layout.pos.get(key);
    if (!p) return;
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  });
  if (minX === Infinity) return null;
  return {
    minX: minX - NODE_W / 2,
    maxX: maxX + NODE_W / 2,
    minY,
    maxY: maxY + NODE_H,
  };
}

// ─── Viewport culling ─────────────────────────────────────────────────────────
export function cullItems(items, rect) {
  const halfW = NODE_W / 2;
  const out = [];
  items.forEach((it) => {
    if (
      it.x + halfW < rect.x0 ||
      it.x - halfW > rect.x1 ||
      it.y + NODE_H < rect.y0 ||
      it.y > rect.y1
    ) {
      return;
    }
    out.push(it);
  });
  return out;
}

const intersects = (g, rect) =>
  !(g.maxX < rect.x0 || g.minX > rect.x1 || g.bottom < rect.y0 || g.top > rect.y1);

// All connectors within `rect` as one SVG path.
export function buildEdgePath(groups, rect) {
  const parts = [];
  groups.forEach((g) => {
    if (!intersects(g, rect)) return;
    if (g.team) {
      if (g.px >= rect.x0 && g.px <= rect.x1) parts.push(`M${g.px} ${g.top}V${g.gy0}`);
      return;
    }
    if (g.px >= rect.x0 && g.px <= rect.x1) parts.push(`M${g.px} ${g.top}V${g.mid}`);
    const bx0 = Math.max(g.minX, rect.x0);
    const bx1 = Math.min(g.maxX, rect.x1);
    if (bx1 > bx0) parts.push(`M${bx0} ${g.mid}H${bx1}`);
    for (let i = 0; i < g.xs.length; i++) {
      const x = g.xs[i];
      if (x < rect.x0) continue;
      if (x > rect.x1) break;
      parts.push(`M${x} ${g.mid}V${g.bottom}`);
    }
  });
  return parts.join("");
}

// Team blocks within `rect`.
export function buildTeamRects(groups, rect) {
  return groups
    .filter((g) => g.team && intersects(g, rect))
    .map((g) => ({ key: g.key, x: g.gx0, y: g.gy0, w: g.gx1 - g.gx0, h: g.gy1 - g.gy0, count: g.count }));
}

// The reporting line of the selected node: every connector from the top of
// the chart down to it, plus the ones to its own direct reports.
export function buildHighlightPath(layout, key) {
  const s = key ? layout.pos.get(key) : null;
  if (!s) return "";
  const parts = [];
  let childKey = key;
  let parentKey = layout.parentOf.get(childKey);
  while (parentKey) {
    const p = layout.pos.get(parentKey);
    const c = layout.pos.get(childKey);
    const pg = layout.groupOf.get(parentKey);
    const top = p.y + NODE_H + 2;
    if (pg.team) {
      parts.push(`M${p.x} ${top}V${pg.gy0}`);
    } else {
      const bottom = c.y - 4;
      parts.push(`M${p.x} ${top}V${(top + bottom) / 2}H${c.x}V${bottom}`);
    }
    childKey = parentKey;
    parentKey = layout.parentOf.get(childKey);
  }
  const g = layout.groupOf.get(key);
  if (g && g.team) {
    parts.push(`M${s.x} ${g.top}V${g.gy0}`);
  } else if (g) {
    layout.kids.get(key).forEach((c) => {
      parts.push(`M${s.x} ${g.top}V${g.mid}H${layout.pos.get(c).x}V${g.bottom}`);
    });
  }
  return parts.join("");
}

// The whole visible tree, scaled into the minimap, as two paths.
export function buildMinimap(layout, width, height, padding = 8) {
  const b = layout.bounds;
  if (!b) return null;
  const rangeX = Math.max(1, b.maxX - b.minX);
  const rangeY = Math.max(1, b.maxY - b.minY);
  const scale = Math.min((width - padding * 2) / rangeX, (height - padding * 2) / rangeY);
  const ox = padding + (width - padding * 2 - rangeX * scale) / 2 - b.minX * scale;
  const oy = padding + (height - padding * 2 - rangeY * scale) / 2 - b.minY * scale;
  const r = (v) => Math.round(v * 10) / 10;
  const w = Math.max(1, NODE_W * scale);
  const h = Math.max(1, NODE_H * scale);
  const sx = (v) => r(v * scale + ox);
  const sy = (v) => r(v * scale + oy);

  const nodeParts = [];
  layout.items.forEach((it) => {
    nodeParts.push(`M${r(it.x * scale + ox - w / 2)} ${sy(it.y)}h${r(w)}v${r(h)}h${r(-w)}z`);
  });
  const edgeParts = [];
  layout.groups.forEach((g) => {
    if (g.team) {
      edgeParts.push(`M${sx(g.px)} ${sy(g.top)}V${sy(g.gy0)}M${sx(g.gx0)} ${sy(g.gy0)}H${sx(g.gx1)}V${sy(g.gy1)}H${sx(g.gx0)}Z`);
      return;
    }
    const mid = sy(g.mid);
    edgeParts.push(`M${sx(g.px)} ${sy(g.top)}V${mid}M${sx(g.minX)} ${mid}H${sx(g.maxX)}`);
    g.xs.forEach((x) => edgeParts.push(`M${sx(x)} ${mid}V${sy(g.bottom)}`));
  });
  return { scale, ox, oy, nodes: nodeParts.join(""), edges: edgeParts.join("") };
}

// ─── Search ───────────────────────────────────────────────────────────────────
export function functionKeyOf(node) {
  if (node.function_id != null && node.function_id !== "") return String(node.function_id);
  if (node.functionName) return `name:${lower(node.functionName)}`;
  return null;
}

// Search entries for every job and every function, built once per data load.
export function buildSearchIndex(index, functions) {
  const fnById = new Map();
  (functions || []).forEach((f) => {
    if (f?.function_id != null) fnById.set(String(f.function_id), f);
  });

  const fnByKey = new Map();
  const ensureFn = (key, fallbackName) => {
    let entry = fnByKey.get(key);
    if (entry) return entry;
    const f = fnById.get(key);
    const name = f?.name || fallbackName || "Unnamed function";
    entry = {
      key,
      name,
      code: f?.function_code || "",
      color: f?.background_color || null,
      parentKey: f?.parent_id != null ? String(f.parent_id) : null,
      jobIds: [],
      nameLower: lower(name),
      secondary: lower(f?.function_code),
    };
    fnByKey.set(key, entry);
    return entry;
  };
  fnById.forEach((_, key) => ensureFn(key));

  const jobs = [];
  const jobById = new Map();
  index.byId.forEach((n) => {
    const fnKey = functionKeyOf(n);
    const fn = fnKey ? ensureFn(fnKey, n.functionName) : null;
    if (fn) fn.jobIds.push(n.id);
    const functionName = fn ? fn.name : "";
    const inTree = index.inTree.has(n.id);
    const entry = {
      key: n.id,
      id: n.id,
      name: n.name || "Untitled",
      code: n.jobCode || "",
      employee: n.employeeName || "",
      functionName,
      inTree,
      depth: inTree ? index.depth.get(n.id) ?? 0 : UNREACHABLE_DEPTH,
      nameLower: lower(n.name),
      secondary: [lower(n.jobCode), lower(functionName), lower(n.employeeName)].join("\u0000"),
    };
    jobs.push(entry);
    jobById.set(n.id, entry);
  });

  const fnList = Array.from(fnByKey.values());
  fnList.forEach((e) => {
    const path = [];
    const seen = new Set([e.key]);
    let p = e.parentKey;
    while (p && fnByKey.has(p) && !seen.has(p)) {
      seen.add(p);
      path.unshift(fnByKey.get(p).name);
      p = fnByKey.get(p).parentKey;
    }
    e.path = path;
    e.jobCount = e.jobIds.length;
    if (path.length) e.secondary += `\u0000${lower(path.join(" "))}`;
  });
  fnList.sort((a, b) => b.jobCount - a.jobCount || compareStr(a.nameLower, b.nameLower));

  return { jobs, jobById, functions: fnList, functionByKey: fnByKey };
}

export function tokenize(query) {
  return lower(query).trim().split(/\s+/).filter(Boolean);
}

const compareStr = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

const isWordChar = (code) =>
  (code >= 48 && code <= 57) || (code >= 97 && code <= 122) || code > 127;

// 0 whole-field match, 1 prefix, 2 word start, 3 anywhere, -1 no match.
function tokenScore(hay, token) {
  let i = hay.indexOf(token);
  if (i < 0) return -1;
  if (i === 0) return hay.length === token.length ? 0 : 1;
  while (i > 0) {
    if (!isWordChar(hay.charCodeAt(i - 1))) return 2;
    i = hay.indexOf(token, i + 1);
  }
  return 3;
}

// Every token must match the name or a secondary field. Name matches rank
// first, then shallower positions in the org, then alphabetical.
export function rankEntries(entries, query) {
  const tokens = tokenize(query);
  if (tokens.length === 0) return entries;
  const full = tokens.join(" ");
  const scored = [];
  entries.forEach((e) => {
    let score = 0;
    for (let t = 0; t < tokens.length; t++) {
      let s = tokenScore(e.nameLower, tokens[t]);
      if (s < 0) s = e.secondary.includes(tokens[t]) ? 6 : -1;
      if (s < 0) return;
      score += s;
    }
    if (e.nameLower === full) score -= 10;
    else if (e.nameLower.startsWith(full)) score -= 5;
    scored.push({ e, score });
  });
  scored.sort(
    (a, b) =>
      a.score - b.score ||
      (a.e.depth || 0) - (b.e.depth || 0) ||
      compareStr(a.e.nameLower, b.e.nameLower),
  );
  return scored.map((s) => s.e);
}

// Jobs of one function, top of the org first.
export function jobsOfFunction(searchIndex, fnKey) {
  const fn = searchIndex.functionByKey.get(fnKey);
  if (!fn) return [];
  return fn.jobIds
    .map((id) => searchIndex.jobById.get(id))
    .filter(Boolean)
    .sort((a, b) => a.depth - b.depth || compareStr(a.nameLower, b.nameLower));
}

// The highest jobs of a function in the tree: those whose manager is outside it.
export function functionHeads(index, jobIds, limit = 8) {
  const members = new Set(jobIds);
  const heads = jobIds.filter((id) => {
    if (!index.inTree.has(id)) return false;
    const parentId = index.byId.get(id)?.parentId;
    return !parentId || !members.has(parentId);
  });
  heads.sort((a, b) => (index.depth.get(a) ?? 0) - (index.depth.get(b) ?? 0));
  return heads.slice(0, limit);
}

// [start, end) ranges of `text` matching any token, merged.
export function matchRanges(text, tokens) {
  if (!text || tokens.length === 0) return [];
  const hay = lower(text);
  const ranges = [];
  tokens.forEach((t) => {
    const i = hay.indexOf(t);
    if (i >= 0) ranges.push([i, i + t.length]);
  });
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  ranges.forEach((r) => {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  });
  return merged;
}

// ─── Job picker options ───────────────────────────────────────────────────────
function toJobOption(j) {
  const id = j?.job_id ?? j?.id;
  if (id == null) return null;
  const value = String(id);
  const label = j.name || `Job #${value}`;
  const code = j.jobCode || j.job_code || "";
  const sub = j.functionName || j.function_name || j.Function?.name || "";
  return {
    value,
    label,
    code,
    sub,
    nameLower: lower(label),
    secondary: `${lower(code)}\u0000${lower(sub)}`,
  };
}

export function toJobOptions(jobs) {
  const out = [];
  const seen = new Set();
  (jobs || []).forEach((j) => {
    const opt = toJobOption(j);
    if (!opt || seen.has(opt.value)) return;
    seen.add(opt.value);
    out.push(opt);
  });
  return out;
}

// The company job list, kept in its order, with names refreshed from the
// hierarchy and any jobs created since it was fetched appended.
export function mergeJobOptions(companyJobs, nodes) {
  const fromNodes = new Map(toJobOptions(nodes).map((o) => [o.value, o]));
  const out = [];
  const seen = new Set();
  toJobOptions(companyJobs).forEach((o) => {
    seen.add(o.value);
    out.push(fromNodes.get(o.value) || o);
  });
  fromNodes.forEach((o, value) => {
    if (!seen.has(value)) out.push(o);
  });
  return out;
}

export const formatCount = (n) => Number(n || 0).toLocaleString();
